import { api, type AdminUser, type TenantAiView, type TenantInfo, type User } from './api';
import { esc, fmtDate, openModal, qs, readFileText, toast } from './ui';
import { aiConnected, aiFieldsHtml, bindAiFields, readAiFields } from './aiForm';
import { paymentDialog } from './billing';
import { t as $t } from '@enercad/core';


/* User administration (admins only): create, rename, role, disable, reset password. */

export function adminView(root: HTMLElement, me: User, topbar: string, bindTop: () => void) {
  if (me.role !== 'admin') {
    root.innerHTML = `<div class="shell">${topbar}<main class="list"><div class="empty">${$t("Bu sayfa yalnızca yöneticiler içindir.")}</div></main></div>`;
    return bindTop();
  }
  root.innerHTML = `<div class="shell">${topbar}<main class="list">
    <div class="list-head"><h1>${$t("Firma ve kullanıcılar")}</h1><button class="btn pri" id="add">${$t("+ Kullanıcı")}</button></div>
    <div class="planbox" id="plan"></div>
    <div class="planbox"><div><span class="note">${$t("Yedek")}</span>${$t("<b>Projeler ve firma kütüphanesi</b>")}</div>
      <div style="display:flex;gap:6px;align-items:center;flex-wrap:wrap"><a class="btn" href="/api/tenant/backup" download title="${$t("Firmanın bütün projeleri, parçaları, ön görünüşleri, sembolleri ve makroları tek dosyada")}">${$t("Yedeği indir")}</a>
      <button class="btn" id="restore" title="${$t("Yedek dosyasındaki projeler yeni kopya olarak eklenir; mevcut projeler değişmez")}">${$t("Yedekten geri yükle")}</button>
      <input type="file" id="bfile" accept=".json,application/json" hidden></div></div>
    <details class="apibox" id="aibox"><summary><b>${$t('Yapay zeka')}</b> <span class="note" id="aisum">${$t('Firmanızın kendi yapay zeka hesabı')}</span></summary>
      <p class="note">${$t('Kendi yapay zeka hesabınızı bağlarsanız (Anthropic Claude, OpenAI ChatGPT, Google Gemini, Mistral, DeepSeek, Grok, Azure OpenAI, OpenRouter ya da kendi sunucunuz) firmanızın yapay zeka özellikleri (projeyi anlatarak oluşturma, tek hattan pano okuma, gözden geçirme, düzeltme önerileri) bu hesapla çalışır ve kullanım ücreti sağlayıcı tarafından doğrudan firmanıza faturalanır. Anahtar sunucuda şifreli saklanır, bir daha tarayıcıya gönderilmez; yalnızca son 4 hanesi gösterilir.')}</p>
      <div id="aiform" style="margin-top:10px"></div>
    </details>
    <details class="apibox"><summary><b>${$t('API erişimi')}</b> <span class="note">${$t('ERP, Excel makrosu ya da kendi yazılımınızdan projelere erişin')}</span></summary>
      <p class="note">${$t('Anahtar, onu oluşturan kullanıcının yetkileriyle ve yalnızca bu firmanın içinde çalışır. Anahtar yalnızca oluşturulduğunda bir kez gösterilir; kaybolursa iptal edip yenisini oluşturun.')}</p>
      <div class="btnrow" style="margin:0 0 8px"><button class="btn pri" id="newkey">${$t('+ API anahtarı')}</button></div>
      <div class="tblw"><table class="t"><thead><tr><th>${$t('Ad')}</th><th>${$t('Anahtar')}</th><th>${$t('Oluşturan')}</th><th>${$t('Son kullanım')}</th><th></th></tr></thead><tbody id="keys"></tbody></table></div>
      <h3 style="margin:14px 0 6px">${$t('Örnekler')}</h3>
      <pre class="apidoc">curl -H "Authorization: Bearer ek_…" ${location.origin}/api/projects
curl -H "Authorization: Bearer ek_…" ${location.origin}/api/projects/&lt;id&gt;
curl -X POST -H "Authorization: Bearer ek_…" -H "Content-Type: application/json" \
     -d '{"name":"Pompa istasyonu"}' ${location.origin}/api/projects
curl -X POST -H "Authorization: Bearer ek_…" -H "Content-Type: application/json" \
     -d '{"list":"M1\tPompa\t4\tDirekt\tPLC\t20"}' ${location.origin}/api/projects/&lt;id&gt;/motors
curl -H "Authorization: Bearer ek_…" -o proje.pdf "${location.origin}/api/projects/&lt;id&gt;/export.pdf?lang=en"</pre>
      <p class="note">${$t('Diğer uçlar: PUT /api/projects/&lt;id&gt; (data, version), GET …/reports.xlsx, …/plc-tags.xlsx, GET /api/parts?q=…, POST /api/parts. Yanıtların dili ?lang= ya da x-lang başlığıyla seçilir.')}</p>
    </details>
    <p class="note" style="margin:0 0 14px">${$t("Firmanızın hesaplarını buradan açın. Projeleri paylaşmak için proje içindeki <b>Paylaş</b> düğmesini kullanın.")}</p>
    <div class="tblw"><table class="t"><thead><tr><th>${$t("Ad")}</th><th>${$t("E-posta")}</th><th>${$t("Rol")}</th><th>${$t("Proje")}</th><th>${$t("Durum")}</th><th>${$t("Son görülme")}</th><th>${$t("Oluşturma")}</th><th></th></tr></thead><tbody id="rows"></tbody></table></div>
  </main></div>`;
  bindTop();
  let users: AdminUser[] = [];

  const showPlan = (t: TenantInfo) => {
    const lim = (n: number, l: number | null) => `${n}${l != null ? ` / ${l}` : ''}`;
    qs(root, '#plan').innerHTML = `<div><span class="note">${$t("Firma")}</span><b>${esc(t.name)}</b> <button class="btn" id="rename" style="padding:1px 7px">${$t("Adı değiştir")}</button></div>
      <div><span class="note">${$t("Plan")}</span><b>${esc(t.planName)}</b></div>
      <div><span class="note">${$t("Bitiş")}</span><b>${t.expires ? `${esc(t.expires)}${t.daysLeft != null ? (t.daysLeft > 0 ? $t(" · {0} gün", t.daysLeft) : $t(" · doldu")) : ''}` : $t("süresiz")}</b></div>
      <div><span class="note">${$t("Kullanıcı")}</span><b>${lim(t.usage.users, t.limits.users)}</b></div>
      <div><span class="note">${$t("Proje")}</span><b>${lim(t.usage.projects, t.limits.projects)}</b></div>
      <div style="margin-left:auto;align-self:center"><button class="btn pri" id="pay">${$t("Plan ve ödeme")}</button></div>`;
  };
  /** "online" when active in the last five minutes, else the date */
  const lastSeen = (s: string | null) => {
    if (!s) return `<span class="note">${$t("hiç giriş yapmadı")}</span>`;
    const ago = Date.now() - Date.parse(s.replace(' ', 'T') + 'Z');
    return ago < 5 * 60_000 ? `<span class="pill ok">${$t("çevrimiçi")}</span>` : esc(fmtDate(s));
  };
  const load = async () => {
    try {
      const r = await api.adminUsers();
      users = r.users;
      if (r.tenant) showPlan(r.tenant);
      qs(root, '#rows').innerHTML = users
        .map(
          (u, i) => `<tr><td>${esc(u.name)}${u.id === me.id ? ` <span class="note">${$t("(siz)")}</span>` : ''}</td><td class="m">${esc(u.email)}</td>
            <td><select data-role="${i}" aria-label="${$t("Rol")}"><option value="user"${u.role === 'user' ? ' selected' : ''}>${$t("Kullanıcı")}</option><option value="admin"${u.role === 'admin' ? ' selected' : ''}>${$t("Yönetici")}</option></select></td>
            <td class="m">${u.projects}</td>
            <td>${u.disabled ? `<span class="pill err">${$t("devre dışı")}</span>` : `<span class="pill ok">${$t("etkin")}</span>`}</td>
            <td>${lastSeen(u.last_seen)}</td>
            <td>${esc(fmtDate(u.created_at))}</td>
            <td style="white-space:nowrap"><button class="btn" data-ed="${i}">${$t("Düzenle")}</button> <button class="btn" data-pw="${i}">${$t("Şifre sıfırla")}</button> ${u.id === me.id ? '' : `<button class="btn${u.disabled ? '' : ' danger'}" data-dis="${i}">${u.disabled ? $t("Etkinleştir") : $t("Devre dışı bırak")}</button>`}</td></tr>`,
        )
        .join('');
    } catch (e) {
      toast((e as Error).message);
    }
  };

  const patch = async (u: AdminUser, body: Parameters<typeof api.adminPatch>[1], msg: string) => {
    try {
      await api.adminPatch(u.id, body);
      toast(msg);
    } catch (e) {
      toast((e as Error).message);
    }
    load();
  };

  root.addEventListener('change', (e) => {
    const t = e.target as HTMLSelectElement;
    if (t.dataset.role == null) return;
    const u = users[+t.dataset.role];
    void patch(u, { role: t.value as 'admin' | 'user' }, $t("{0}: rol güncellendi", u.name));
  });

  root.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    const dis = t.closest<HTMLElement>('[data-dis]')?.dataset.dis;
    if (dis != null) {
      const u = users[+dis];
      if (u.disabled) return void patch(u, { disabled: false }, $t("{0} etkinleştirildi", u.name));
      const close = openModal(
        `<h2>${$t("{0} devre dışı bırakılsın mı?", esc(u.name))}</h2><p>${$t("Hesap giriş yapamaz ve açık oturumları kapanır. Projeleri silinmez; istediğiniz zaman yeniden etkinleştirebilirsiniz.")}</p>
        <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="ok">${$t("Devre dışı bırak")}</button></div>`,
        (a) => {
          if (a !== 'ok') return;
          close();
          void patch(u, { disabled: true }, $t("{0} devre dışı bırakıldı", u.name));
        },
      );
      return;
    }
    const ed = t.closest<HTMLElement>('[data-ed]')?.dataset.ed;
    if (ed != null) {
      const u = users[+ed];
      const close = openModal(
        `<h2>${esc(u.name)}</h2>
        <label class="fld"><span>${$t("Ad soyad")}</span><input id="en" value="${esc(u.name)}"></label>
        <label class="fld"><span>${$t("E-posta (giriş adı)")}</span><input id="ee" type="email" value="${esc(u.email)}" autocapitalize="none" spellcheck="false"></label>
        <p class="note">${$t("E-postayı değiştirirseniz kullanıcı bundan sonra yeni adresle giriş yapar; şifresi değişmez.")}</p>
        <div class="err" id="eerr"></div>
        <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="ok">${$t("Kaydet")}</button></div>`,
        async (a, dlg) => {
          if (a !== 'ok') return;
          try {
            await api.adminPatch(u.id, { name: qs<HTMLInputElement>(dlg, '#en').value, email: qs<HTMLInputElement>(dlg, '#ee').value.trim().toLowerCase() });
            close();
            toast($t("Kullanıcı güncellendi"));
            load();
          } catch (x) {
            qs(dlg, '#eerr').textContent = (x as Error).message;
          }
        },
      );
      return;
    }
    const pw = t.closest<HTMLElement>('[data-pw]')?.dataset.pw;
    if (pw != null) {
      const u = users[+pw];
      const close = openModal(
        `<h2>${$t("{0} için yeni şifre", esc(u.name))}</h2><p>${$t("Kullanıcının açık oturumları kapanır. Yeni şifreyi kullanıcıya güvenli bir yoldan iletin.")}</p>
        <label class="fld"><span>${$t("Yeni şifre")}</span><input id="np" type="password" autocomplete="new-password" minlength="8"></label>
        <div class="err" id="perr"></div>
        <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="ok">${$t("Şifreyi değiştir")}</button></div>`,
        async (a, dlg) => {
          if (a !== 'ok') return;
          const v = qs<HTMLInputElement>(dlg, '#np').value;
          if (v.length < 8) return void (qs(dlg, '#perr').textContent = $t("Şifre en az 8 karakter olmalı."));
          close();
          await patch(u, { password: v }, $t("Şifre değiştirildi"));
        },
      );
      return;
    }
    if (t.id === 'pay') return void paymentDialog(() => void load());
    if (t.id === 'restore') return qs<HTMLInputElement>(root, '#bfile').click();
    if (t.id === 'rename') {
      const close = openModal(
        `<h2>${$t("Firma adı")}</h2><label class="fld"><span>${$t("Firma adı")}</span><input id="tn"></label><div class="err" id="terr"></div>
        <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="ok">${$t("Kaydet")}</button></div>`,
        async (a, dlg) => {
          if (a !== 'ok') return;
          try {
            await api.renameTenant(qs<HTMLInputElement>(dlg, '#tn').value);
            close();
            location.reload();
          } catch (e) {
            qs(dlg, '#terr').textContent = (e as Error).message;
          }
        },
      );
      return;
    }
    if (t.id === 'add') {
      const close = openModal(
        `<h2>${$t("Yeni kullanıcı")}</h2>
        <label class="fld"><span>${$t("Ad soyad")}</span><input id="un" autocomplete="off"></label>
        <label class="fld"><span>${$t("E-posta")}</span><input id="ue" type="email" autocomplete="off" autocapitalize="none"></label>
        <label class="fld"><span>${$t("İlk şifre")}</span><input id="up" type="password" autocomplete="new-password" minlength="8"></label>
        <label class="fld"><span>${$t("Rol")}</span><select id="ur"><option value="user">${$t("Kullanıcı")}</option><option value="admin">${$t("Yönetici")}</option></select></label>
        <p class="note">${$t("Kullanıcı giriş yaptıktan sonra şifresini kendisi değiştirebilir.")}</p>
        <div class="err" id="uerr"></div>
        <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="ok">${$t("Oluştur")}</button></div>`,
        async (a, dlg) => {
          if (a !== 'ok') return;
          const v = (id: string) => qs<HTMLInputElement>(dlg, '#' + id).value.trim();
          try {
            await api.adminCreate({ name: v('un'), email: v('ue'), password: qs<HTMLInputElement>(dlg, '#up').value, role: v('ur') as 'admin' | 'user' });
            close();
            toast($t("Kullanıcı oluşturuldu"));
            load();
          } catch (e) {
            qs(dlg, '#uerr').textContent = (e as Error).message;
          }
        },
      );
    }
  });
  const bfile = qs<HTMLInputElement>(root, '#bfile');
  bfile.onchange = async () => {
    const f = bfile.files?.[0];
    bfile.value = '';
    if (!f) return;
    let data: unknown;
    try {
      data = JSON.parse(await readFileText(f));
    } catch {
      return toast($t("Dosya geçerli bir JSON değil."));
    }
    const b = data as { company?: string; exported_at?: string; projects?: unknown[] };
    const close = openModal(
      `<h2>${$t("Yedekten geri yükle")}</h2><p>${$t("<b>{0}</b> firmasının {1} tarihli yedeği: {2} proje.", esc(b.company ?? '?'), esc(fmtDate(b.exported_at ?? '')), b.projects?.length ?? 0)}</p>
      <p>${$t("Projeler yeni kopya olarak eklenir (aynı adlı proje varsa adına \"(yedekten)\" eklenir). Parçalar, sembol ve makrolar firmanızın kütüphanesine eklenir; aynı parça numaraları yedektekiyle güncellenir.")}</p>
      <div class="err" id="rerr"></div>
      <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="ok">${$t("Geri yükle")}</button></div>`,
      async (a, dlg) => {
        if (a !== 'ok') return;
        try {
          const r = await api.restore(data);
          close();
          toast($t("Geri yüklendi: {0} proje, {1} parça, {2} sembol, {3} makro", r.projects, r.parts, r.symbols, r.macros));
          load();
        } catch (e) {
          qs(dlg, '#rerr').textContent = (e as Error).message;
        }
      },
    );
  };
  const keys = async () => {
    try {
      const { keys: list } = await api.apiKeys();
      qs(root, '#keys').innerHTML =
        list
          .map(
            (k) => `<tr${k.revoked_at ? ' class="off"' : ''}><td>${esc(k.name)}</td><td class="m">${esc(k.prefix)}…</td><td>${esc(k.owner)}</td>
            <td>${k.revoked_at ? `<span class="pill err">${$t('iptal')}</span>` : k.last_used ? esc(fmtDate(k.last_used)) : '—'}</td>
            <td>${k.revoked_at ? '' : `<button class="btn danger" data-revoke="${k.id}" data-name="${esc(k.name)}">${$t('İptal et')}</button>`}</td></tr>`,
          )
          .join('') || `<tr><td colspan="5"><div class="empty">${$t('Henüz API anahtarı yok.')}</div></td></tr>`;
    } catch (e) {
      qs(root, '#keys').innerHTML = `<tr><td colspan="5" class="err">${esc((e as Error).message)}</td></tr>`;
    }
  };
  root.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t.id === 'newkey') {
      const close = openModal(
        `<h2>${$t('Yeni API anahtarı')}</h2><label class="fld"><span>${$t('Ad')}</span><input id="kn" placeholder="${$t('ör. ERP entegrasyonu')}"></label><div class="err" id="ke"></div>
        <div class="acts"><button class="btn" data-m="close">${$t('Vazgeç')}</button><button class="btn pri" data-m="ok">${$t('Oluştur')}</button></div>`,
        async (a, dlg) => {
          if (a !== 'ok') return;
          try {
            const r = await api.createApiKey(qs<HTMLInputElement>(dlg, '#kn').value);
            close();
            void keys();
            openModal(
              `<h2>${$t('API anahtarınız')}</h2><p>${$t('Bu anahtarı şimdi kopyalayın; bir daha gösterilmeyecek. Anahtarı bilen herkes sizin yetkilerinizle erişebilir, güvenli bir yerde saklayın.')}</p>
              <input class="m" id="kv" value="${esc(r.key)}" readonly style="width:100%">
              <div class="acts"><button class="btn" data-m="copy">${$t('Kopyala')}</button><button class="btn pri" data-m="close">${$t('Tamam')}</button></div>`,
              async (b, d2) => {
                if (b !== 'copy') return;
                await navigator.clipboard?.writeText(qs<HTMLInputElement>(d2, '#kv').value).catch(() => {});
                toast($t('Anahtar kopyalandı'));
              },
            );
          } catch (x) {
            qs(dlg, '#ke').textContent = (x as Error).message;
          }
        },
      );
      return;
    }
    const rv = t.closest<HTMLElement>('[data-revoke]');
    if (rv) {
      const close = openModal(
        `<h2>${$t('"{0}" anahtarı iptal edilsin mi?', esc(rv.dataset.name ?? ''))}</h2><p>${$t('Bu anahtarı kullanan sistemler hemen erişimini kaybeder. İşlem geri alınamaz.')}</p>
        <div class="acts"><button class="btn" data-m="close">${$t('Vazgeç')}</button><button class="btn pri" data-m="ok">${$t('İptal et')}</button></div>`,
        async (a) => {
          if (a !== 'ok') return;
          close();
          try {
            await api.revokeApiKey(Number(rv.dataset.revoke));
            toast($t('Anahtar iptal edildi'));
          } catch (x) {
            toast((x as Error).message);
          }
          void keys();
        },
      );
    }
  });
  /* the company's own AI key */
  const aiForm = (v: TenantAiView) => {
    const s = v.settings;
    qs(root, '#aisum').innerHTML = aiConnected(v)
      ? `<span class="pill ${s.enabled ? 'ok' : 'warn'}">${s.enabled ? $t('kendi hesabınız kullanılıyor') : $t('kapalı')}</span> ${$t('bu ay {0} istek', v.used)}`
      : esc($t('Firmanızın kendi yapay zeka hesabı'));
    qs(root, '#aiform').innerHTML = `
      ${aiFieldsHtml(v)}
      <label class="chkline"><input type="checkbox" id="taen"${s.enabled ? ' checked' : ''}> ${$t('Yapay zeka özelliklerinde bu anahtarı kullan')}</label>
      <label class="fld"><span>${$t('Aylık istek sınırı (0 = sınırsız)')}</span><input id="talim" inputmode="numeric" value="${s.monthlyLimit}"></label>
      <p class="note" id="tares"></p>
      <div class="btnrow" style="margin:0"><button class="btn pri" id="tasave">${$t('Kaydet')}</button><button class="btn" id="tatest"${aiConnected(v) ? '' : ' disabled'}>${$t('Bağlantıyı dene')}</button>${v.keySet ? `<button class="btn danger" id="tadel">${$t('Anahtarı sil')}</button>` : ''}</div>`;
    bindAiFields(qs(root, '#aiform'), v);
  };
  const aiLoad = () => api.tenantAi().then(aiForm, (e) => (qs(root, '#aiform').innerHTML = `<p class="err">${esc((e as Error).message)}</p>`));
  const aiSave = async (apiKey?: string) => {
    const out = qs(root, '#tares');
    const { key: _key, ...f } = readAiFields(qs(root, '#aiform'));
    try {
      aiForm(await api.saveTenantAi({
        enabled: qs<HTMLInputElement>(root, '#taen').checked,
        ...f,
        monthlyLimit: Number(qs<HTMLInputElement>(root, '#talim').value) || 0,
        ...(apiKey !== undefined ? { apiKey } : {}),
      }));
      toast($t('Yapay zeka ayarları kaydedildi'));
    } catch (e) {
      out.textContent = (e as Error).message;
    }
  };
  root.addEventListener('click', async (e) => {
    const t = e.target as HTMLElement;
    if (t.id === 'tasave') {
      return void aiSave(readAiFields(qs(root, '#aiform')).key || undefined);
    }
    if (t.id === 'tatest') {
      const out = qs(root, '#tares');
      out.textContent = $t('Deneniyor…');
      try {
        const r = await api.testTenantAi();
        out.innerHTML = r.ok ? `<span class="pill ok">${$t('Bağlantı çalışıyor')}</span> ${$t('{0} ms · yanıt: {1}', r.ms, esc(r.msg))}` : `<span class="pill err">${$t('Hata')}</span> ${esc(r.msg)}`;
      } catch (x) {
        out.textContent = (x as Error).message;
      }
      return;
    }
    if (t.id === 'tadel') {
      const close = openModal(
        `<h2>${$t('Firma anahtarı silinsin mi?')}</h2><p>${$t('Yapay zeka özellikleri firmanızda bu anahtarla çalışmaz; platform tarafından açılmışsa platformun anahtarı kullanılır.')}</p>
        <div class="acts"><button class="btn" data-m="close">${$t('Vazgeç')}</button><button class="btn pri" data-m="ok">${$t('Anahtarı sil')}</button></div>`,
        async (a) => {
          if (a !== 'ok') return;
          close();
          await aiSave('');
        },
      );
    }
  });
  void aiLoad();
  void keys();
  load();
  // link from the plan banner or a reminder mail opens the payment dialog once
  if (location.hash === '#/admin/odeme') {
    history.replaceState(null, '', '#/admin');
    void paymentDialog(() => void load());
  }
}

/** "Change my password" dialog, available from the top bar. */
export function passwordDialog() {
  const close = openModal(
    `<h2>${$t("Şifremi değiştir")}</h2>
    <label class="fld"><span>${$t("Mevcut şifre")}</span><input id="cp" type="password" autocomplete="current-password"></label>
    <label class="fld"><span>${$t("Yeni şifre (en az 8 karakter)")}</span><input id="np" type="password" autocomplete="new-password" minlength="8"></label>
    <label class="fld"><span>${$t("Yeni şifre (tekrar)")}</span><input id="np2" type="password" autocomplete="new-password" minlength="8"></label>
    <div class="err" id="perr"></div>
    <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="ok">${$t("Değiştir")}</button></div>`,
    async (a, dlg) => {
      if (a !== 'ok') return;
      if (qs<HTMLInputElement>(dlg, '#np').value !== qs<HTMLInputElement>(dlg, '#np2').value) return void (qs(dlg, '#perr').textContent = $t("Yeni şifreler aynı değil."));
      try {
        await api.changePassword(qs<HTMLInputElement>(dlg, '#cp').value, qs<HTMLInputElement>(dlg, '#np').value);
        close();
        toast($t("Şifreniz değiştirildi"));
      } catch (e) {
        qs(dlg, '#perr').textContent = (e as Error).message;
      }
    },
  );
}
